import { ProxyRole } from '../contexts/ProxyRoleContext';

// ─────────────────────────────────────────────────────────────────────────────
// Office-role scoping for the soft launch (pre-true-RBAC, GH #442).
//
// This is NOT full RBAC — backend endpoints stay open and true role enforcement
// is deferred until untrusted staff are onboarded (#442). For the soft launch we
// only narrow the *Office* role in the admin UI: hide its nav links AND redirect
// direct-URL access so a hidden surface can't be reached by typing the path.
//
// Single source of truth consumed by:
//   - App.tsx route guard          (RequireRouteAccess)  → security/redirect
//   - LeftRail / MobileNavDrawer   (canSeeNavPath)       → link visibility
//
// Role model (unchanged except Office):
//   admin / owner   → everything
//   dispatcher      → full route access; nav stays curated to customers/orders/loadboard
//   driver          → dashboard only
//   office          → the allow-list below, nothing else
// ─────────────────────────────────────────────────────────────────────────────

// Surfaces the Office role may reach. Matched as path prefixes (case-insensitive),
// so '/orders' also covers '/orders/new' and '/orders/:id'.
const OFFICE_ROUTE_PREFIXES = [
  '/customers',
  '/orders',
  '/projects',
  '/documents',
  '/financial',
  '/messaging',
  '/crm',
  '/contact-submissions',
  '/leads',
  '/loadboard',
  '/search',
  '/profile',
];

const FULL_ACCESS_ROLES: ProxyRole[] = ['admin', 'owner', 'dispatcher'];

// TEMPORARY (pre-launch declutter): these surfaces are hidden from the Office
// left rail / mobile nav for the soft launch, even though Office keeps route &
// contextual access (e.g. opening a document from an order still works). Remove
// this list post-launch to restore the nav links — no other change needed.
const OFFICE_NAV_HIDDEN_PRELAUNCH = ['/messaging', '/documents', '/loadboard', '/financial'];

function matchesPrefix(pathname: string, prefixes: string[]): boolean {
  const p = pathname.toLowerCase();
  return prefixes.some((prefix) => p === prefix || p.startsWith(prefix + '/'));
}

/**
 * Route-level access (drives the redirect guard). dispatcher keeps full route
 * access for the soft launch — only its *nav* is curated. Driver is dashboard
 * (the ungated index route) only. Office is restricted to OFFICE_ROUTE_PREFIXES.
 */
export function canAccessRoute(role: ProxyRole, pathname: string): boolean {
  if (FULL_ACCESS_ROLES.includes(role)) return true;
  // The dashboard (index / home) is available to every authenticated role.
  if (pathname === '/' || pathname.toLowerCase() === '/home') return true;
  if (role === 'driver') return false; // dashboard only
  // office (and any unknown role, which falls back to the most-restricted view)
  return matchesPrefix(pathname, OFFICE_ROUTE_PREFIXES);
}

/**
 * Nav-link visibility (drives LeftRail + MobileNavDrawer). Filters each surface's
 * full item list down to what the role should see.
 *   - admin/owner → all
 *   - driver      → none
 *   - dispatcher  → curated (customers / orders / loadboard) — prior behavior
 *   - office      → exactly the access-allowed subset that has a nav entry
 */
export function canSeeNavPath(role: ProxyRole, path: string): boolean {
  if (role === 'admin' || role === 'owner') return true;
  if (role === 'driver') return false;
  if (role === 'dispatcher') {
    return matchesPrefix(path, ['/customers', '/orders', '/loadboard']);
  }
  // office: nav shows the access-allowed subset, minus the pre-launch hidden set
  if (matchesPrefix(path, OFFICE_NAV_HIDDEN_PRELAUNCH)) return false;
  return canAccessRoute('office', path);
}
